import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useMatch} from 'react-router';

import {
  LoadingButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewSelectField,
} from '@edna/components';

import {LeftButton} from 'src/components/primitives';
import PATHS from 'src/paths';
import {prepareOptionsForSelect} from 'src/utils';
import {joiResolver} from 'src/utils/validator';

import {useChangePersonalManagerMutation, useGetAccountManagersQuery} from './api';

type TProps = {
  closeModal: TEmptyFunction;
  responsibleManagerId?: number;
};

const resolver = joiResolver((joi) =>
  joi.object({
    responsibleManagerId: joi.number().required(),
  }),
);

export const ResponsibleManagerModal = React.memo<TProps>(({closeModal, responsibleManagerId}) => {
  const {t} = useTranslation();

  const supportTenantPath = useMatch({path: PATHS.SUPPORT_CLIENT_CARD, end: false});
  const tenantLeadPath = useMatch({path: PATHS.COMPANY_CLIENT_CARD, end: false});

  const {data: accountManagers = []} = useGetAccountManagersQuery();
  const [changePersonalManager, {isSuccess, isLoading}] = useChangePersonalManagerMutation();

  const methods = useForm<{responsibleManagerId?: number}>({
    mode: 'all',
    defaultValues: {responsibleManagerId},
    resolver,
  });

  React.useEffect(() => {
    if (isSuccess) {
      closeModal();
    }
  }, [isSuccess, closeModal]);

  const handleChangePersonalManager = React.useCallback(
    (values: {responsibleManagerId?: number}) => {
      if (!values.responsibleManagerId) {
        return;
      }

      changePersonalManager({
        supportTenantId: supportTenantPath?.params.id,
        tenantLeadId: tenantLeadPath?.params.id,
        responsibleManagerId: values.responsibleManagerId,
      });
    },
    [changePersonalManager, supportTenantPath, tenantLeadPath],
  );

  return (
    <Modal width="545px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleChangePersonalManager)}>
          <ModalHeader>
            {t('LeadCompanyClients:leadClientCard.responsibleManagerModal.title')}
          </ModalHeader>
          <ModalContent>
            <NewSelectField
              name="responsibleManagerId"
              label={t('LeadCompanyClients:leadClientCard.responsibleManagerModal:selectManager')}
              options={prepareOptionsForSelect(accountManagers)}
            />
          </ModalContent>
          <ModalActions>
            <LeftButton onClick={closeModal} data-selector="Common:button.update">
              {t('Common:button.cancel')}
            </LeftButton>
            <LoadingButton
              loading={isLoading}
              disabled={isLoading}
              appearance="primary"
              type="submit"
              data-selector="Common:button.update"
            >
              {t('Common:button.update')}
            </LoadingButton>
          </ModalActions>
        </form>
      </FormProvider>
    </Modal>
  );
});

ResponsibleManagerModal.displayName = 'ResponsibleManagerModal';
